import React, { useEffect, useState } from 'react';
import { authFetch } from '../auth';

const emptyForm = {
  id: null,
  login: '',
  first_name: '',
  last_name: '',
  cell_phone: '',
  email: '',
  timeout: 30,
  role: 2,
  theme: 'light',
  password: '',
};

const UsersView = ({ API_URL }) => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [allRights, setAllRights] = useState([]); // [{code, name}]
  const [selectedRights, setSelectedRights] = useState([]); // [code]

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_URL}/admin/users`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setIsEdit(false);
    setForm(emptyForm);
    setError('');
    setModalOpen(true);
    // new user: no rights preselected
    setSelectedRights([]);
  };

  const onEdit = (u) => {
    setIsEdit(true);
    setForm({ ...emptyForm, ...u, password: '' });
    setError('');
    setModalOpen(true);
    // load rights for this user
    loadUserRights(u.id);
  };

  const onCancel = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const isEdit = !!form.id;
      const payload = { ...form };
      if (!isEdit && !payload.password) {
        setError('Password is required for new users');
        setLoading(false);
        return;
      }
      // Avoid sending empty password on edit if unchanged
      if (isEdit && !payload.password) delete payload.password;

      const res = await authFetch(`${API_URL}/admin/users${isEdit ? `/${form.id}` : ''}` , {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, rights: selectedRights }),
      });
      if (!res.ok) {
        const msg = (await res.json()).message || 'Request failed';
        throw new Error(msg);
      }
      await load();
      setForm(emptyForm);
      setModalOpen(false);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (id) => {
    if (!confirm('Delete this user?')) return;
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_URL}/admin/users/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await load();
      if (form.id === id) setForm(emptyForm);
    } catch (e) {
      setError(e.message || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const loadRights = async () => {
    try {
      const res = await authFetch(`${API_URL}/admin/rights`);
      const data = await res.json();
      setAllRights(Array.isArray(data) ? data : []);
    } catch (_) {}
  };

  const loadUserRights = async (userId) => {
    try {
      const res = await authFetch(`${API_URL}/admin/users/${userId}/rights`);
      const data = await res.json();
      setSelectedRights(Array.isArray(data?.codes) ? data.codes : []);
    } catch (_) {
      setSelectedRights([]);
    }
  };

  useEffect(() => { loadRights(); }, []);

  const toggleRight = (code) => {
    setSelectedRights((prev) => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  return (
    <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>Users</h2>
            <button onClick={openAdd} style={btn}>+ Add User</button>
          </div>
      {error && (
        <div style={{ background: '#fdecea', color: '#b71c1c', padding: 8, borderRadius: 4, marginBottom: 10 }}>
          {error}
        </div>
      )}
      <div>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
          <thead>
            <tr style={{ background: 'var(--table-head-bg)' }}>
              {['Login', 'First', 'Last', 'Cell', 'Email', 'Role', 'Theme', 'Timeout', 'Actions'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid var(--border)', color: 'var(--text)', fontSize: 14 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id}>
                <td style={td}>{u.login}</td>
                <td style={td}>{u.first_name}</td>
                <td style={td}>{u.last_name}</td>
                <td style={td}>{u.cell_phone}</td>
                <td style={td}>{u.email}</td>
                <td style={td}>{u.role === 1 ? 'Admin' : 'User'}</td>
                <td style={td}>{u.theme}</td>
                <td style={td}>{u.timeout}</td>
                <td style={td}>
                  <button onClick={() => onEdit(u)} className="btn" style={btn}>Edit</button>
                  <button onClick={() => onDelete(u.id)} className="btn btn-delete" style={{ ...btn, background: 'var(--danger)' }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal for Add/Edit */}
      {modalOpen && (
        <div style={modalOverlay}>
          <div style={modalDialog}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{isEdit ? 'Edit User' : 'Add User'}</h3>
            </div>
            <form onSubmit={onSubmit}>
              <div style={formRow}>
                <label style={label}>Login</label>
                <input style={input} value={form.login} onChange={e => setForm({ ...form, login: e.target.value })} required />
              </div>
              <div style={formRow}>
                <label style={label}>First Name</label>
                <input style={input} value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} required />
              </div>
              <div style={formRow}>
                <label style={label}>Last Name</label>
                <input style={input} value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} />
              </div>
              <div style={formRow}>
                <label style={label}>Cell Phone</label>
                <input style={input} value={form.cell_phone} onChange={e => setForm({ ...form, cell_phone: e.target.value })} />
              </div>
              <div style={formRow}>
                <label style={label}>Email</label>
                <input type="email" style={input} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div style={formRow}>
                <label style={label}>Role</label>
                <select style={input} value={form.role} onChange={e => setForm({ ...form, role: Number(e.target.value) })}>
                  <option value={1}>Admin</option>
                  <option value={2}>User</option>
                </select>
              </div>
              <div style={formRow}>
                <label style={label}>Theme</label>
                <select style={input} value={form.theme} onChange={e => setForm({ ...form, theme: e.target.value })}>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>
              <div style={formRow}>
                <label style={label}>Timeout (min)</label>
                <input type="number" min="1" max="240" style={input} value={form.timeout} onChange={e => setForm({ ...form, timeout: Number(e.target.value) })} />
              </div>
              <div style={formRow}>
                <label style={label}>{isEdit ? 'New Password' : 'Password'}</label>
                <input type="password" style={input} value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder={isEdit ? '(leave blank to keep)' : ''} />
              </div>
              {allRights.length > 0 && (
                <div style={{ ...formRow, marginTop: 6 }}>
                  <label style={label}>Rights</label>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {allRights.map(r => (
                      <label key={r.code} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                        <input
                          type="checkbox"
                          style={{ width: 16, height: 16, accentColor: '#1565c0' }}
                          checked={selectedRights.includes(r.code)}
                          onChange={() => toggleRight(r.code)}
                        />
                        {r.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <button type="submit" className="btn" style={btn} disabled={loading}>{isEdit ? 'Save' : 'Add'}</button>
                <button type="button" onClick={onCancel} className="btn-ghost" style={{ marginLeft: 0 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const td = { padding: '8px 10px', borderBottom: '1px solid var(--border)', color: 'var(--text)' };
const btn = { padding: '6px 10px', background: 'var(--primary)', color: 'var(--primary-contrast)', border: 'none', borderRadius: 4, cursor: 'pointer', marginRight: 6 };
const formRow = { display: 'flex', flexDirection: 'column', marginBottom: 10 };
const label = { fontSize: 12, color: 'var(--text)', marginBottom: 4 };
const input = {
  padding: '10px 12px',
  border: '1px solid var(--border-soft)',
  borderRadius: 6,
  background: 'var(--input-bg-soft)',
  color: 'var(--text)',
  outline: 'none',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.03)'
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
};

const modalDialog = {
  width: '520px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  background: 'var(--modal-bg)',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
};

export default UsersView;
